import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useStorefront } from '../../context/StoreContext.js';
import { normalizeAnalyticsPath } from '../../config/languages.js';
import { sendPageView } from '../../api/analyticsApi.js';
import { isBotUserAgent } from '../../lib/storefront/analyticsBots.js';

// Off unless a build explicitly turns it on, and .env.example pins a real tenant store id —
// so a developer's npm start cannot write into a live customer's dashboard.
const ANALYTICS_ENABLED = (
    process.env.REACT_APP_ENABLE_ANALYTICS ||
    (process.env.NODE_ENV === 'production' ? 'true' : 'false')
).toLowerCase() === 'true';

const IS_BOT =
    typeof navigator !== 'undefined' &&
    (isBotUserAgent(navigator.userAgent) || navigator.webdriver === true);

const MAX_TITLE_LENGTH = 120;

// Module scope rather than a ref: StoreProvider unmounts its children while it re-resolves
// the store, which would wipe a ref and let the same page report twice.
let lastPageViewKey = null;

/** Runs the callback once the document is activated and visible, deferring if it is not. */
function whenPageIsActive(callback) {
    let dispatched = false;
    let detach = null;

    function detachListener() {
        if (detach) {
            detach();
            detach = null;
        }
    }

    function listen(eventName) {
        detachListener();
        const handler = () => {
            detachListener();
            attempt();
        };
        document.addEventListener(eventName, handler);
        detach = () => document.removeEventListener(eventName, handler);
    }

    function attempt() {
        if (dispatched) return;

        if (typeof document === 'undefined') {
            dispatched = true;
            callback();
            return;
        }

        if (document.prerendering) {
            listen('prerenderingchange');
            return;
        }

        if (document.visibilityState && document.visibilityState !== 'visible') {
            listen('visibilitychange');
            return;
        }

        detachListener();
        dispatched = true;
        callback();
    }

    attempt();

    return {
        cancel: detachListener,
        get dispatched() {
            return dispatched;
        },
    };
}

/** Sends one pageview to POST /api/analytics/pageview per normalized route entry. */
export default function PageViewBeacon() {
    const { pathname } = useLocation();
    const { store } = useStorefront();
    const storeId = store?.id || null;
    const normalized = useMemo(() => normalizeAnalyticsPath(pathname).path, [pathname]);

    useEffect(() => {
        if (!ANALYTICS_ENABLED || IS_BOT || !storeId) return undefined;

        const key = `${storeId}|${normalized}`;
        if (lastPageViewKey === key) return undefined;
        // Claimed synchronously: StrictMode's two mounts run back to back in one tick, so
        // claiming it from inside a promise would lose the race and send twice.
        lastPageViewKey = key;

        const gate = whenPageIsActive(() => {
            // <Routes> commits before this component, so a redirect-only route (/login ->
            // /en/sign-in) has already rewritten history while useLocation() still reports
            // the old path. Reporting it would invent a page view for a route that renders
            // nothing and push the session past the bounce threshold.
            if (normalizeAnalyticsPath(window.location.pathname).path !== normalized) {
                lastPageViewKey = null;
                return;
            }

            sendPageView(
                {
                    path: normalized,
                    // pathname only, never search: reset-password and unsubscribe tokens
                    // travel in the query string and nothing reads page.url anyway.
                    // Repeated slashes are collapsed because the server resolves this with
                    // new URL(), and a leading '//' either throws (a 500) or is parsed as a
                    // foreign host.
                    url: pathname.replace(/\/{2,}/g, '/'),
                    title: String(document.title || '').slice(0, MAX_TITLE_LENGTH),
                },
                storeId,
            );
        });

        return () => {
            gate.cancel();
            // Release the key only when nothing was sent, or a StrictMode remount would
            // suppress the real send on the deferred path.
            if (!gate.dispatched && lastPageViewKey === key) {
                lastPageViewKey = null;
            }
        };
    }, [storeId, normalized, pathname]);

    return null;
}
